import { ComponentProps, useMemo } from 'react';
import { IntlShape, useIntl } from 'react-intl';
import { GridColDef } from '@mui/x-data-grid';
import Chip from '@mui/material/Chip';
import { LightChip } from '@atoms/dataDisplay';
import { Box, IconButton, Stack, Theme, Tooltip, useTheme } from '@mui/material';
import { TemplateListItem } from '@libs/models/shiftTemplates';
import dayjs from 'dayjs';
import { FaIcon } from '@atoms/icons';
import { useShiftTemplateDrawerStore } from '@libs/store/shiftTemplates';
import { ShiftType } from '@pages/schedule/components';

type TableColumnsProps = {
  localizationSubset: string;
};
export function useShiftTemplatesListColumns({ localizationSubset }: TableColumnsProps) {
  const intl = useIntl();
  const theme = useTheme();
  const { openDrawer } = useShiftTemplateDrawerStore();
  // Columns should be memoized to prevent table flickering and state loss
  return useMemo(() => {
    const columns: GridColDef<TemplateListItem>[] = [
      getNameColumn(intl, localizationSubset),
      getRecurrenceTypeColumn(intl, localizationSubset, theme),
      getShiftTypeColumn(intl, localizationSubset),
      getQualificationsColumn(intl, localizationSubset, (entity: TemplateListItem) =>
        openDrawer(entity.id, entity, 'copy'),
      ),
    ];
    return columns;
  }, [intl]);
}

function getNameColumn(intl: IntlShape, localizationSubset: string): GridColDef<TemplateListItem> {
  return {
    field: 'name',
    headerName: intl.formatMessage({ id: `${localizationSubset}.table.fields.name.heading` }),
    minWidth: 160,
    resizable: true,
    display: 'flex',
    flex: 1,
    sortable: true,
    filterable: false,
    disableColumnMenu: true,
    renderCell: (params) => {
      if (!params.value) {
        return null;
      }
      return (
        <Stack
          direction={'column'}
          sx={{ height: '100%', overflowX: 'hidden', textOverflow: 'ellipsis' }}
          justifyContent={'center'}
          alignContent={'center'}
        >
          {params.value}
        </Stack>
      );
    },
  };
}

function getShiftTypeColumn(intl: IntlShape, localizationSubset: string): GridColDef<TemplateListItem> {
  return {
    field: 'startDate',
    headerName: intl.formatMessage({
      id: `${localizationSubset}.table.fields.shiftType.heading`,
    }),
    sortable: false,
    width: 160,
    renderCell: (params) => {
      // if (!params.value) {
      //   return null;
      // }
      const row = params.row as TemplateListItem;
      return (
        <ShiftType
          startTime={dayjs(row.startTime, 'HH:mm:ss')}
          endTime={dayjs(row.startTime, 'HH:mm:ss').add(row.durationInSeconds, 'seconds')}
          boxProps={{ height: '100%' }}
        />
      );
    },
  };
}

function getRecurrenceTypeColumn(
  intl: IntlShape,
  localizationSubset: string,
  theme: Theme,
): GridColDef<TemplateListItem> {
  const statusColors: Record<TemplateListItem['recurrenceType'], ComponentProps<typeof LightChip>['color']> = {
    single: 'default',
    series: 'warning',
    rotation: 'info',
  };
  return {
    field: 'recurrenceType',
    headerName: intl.formatMessage({ id: `${localizationSubset}.table.fields.recurrenceType.heading` }),
    minWidth: 160,
    resizable: false,
    sortable: true,
    filterable: false,
    disableColumnMenu: true,
    renderCell: (params) => {
      if (!params.value) {
        return null;
      }
      const row = params.row as TemplateListItem;
      return (
        <Chip
          color={statusColors[row.recurrenceType]}
          variant="outlined"
          label={intl.formatMessage({
            id: `${localizationSubset}.table.fields.recurrenceType.options.${row.recurrenceType.toLowerCase()}`,
          })}
        />
      );
    },
  };
}

function getQualificationsColumn(
  intl: IntlShape,
  localizationSubset: string,
  onCopyClick: (entity: TemplateListItem) => void,
): GridColDef<TemplateListItem> {
  const handleCopyClick = (entity: TemplateListItem) => {
    onCopyClick && onCopyClick(entity);
  };
  return {
    field: 'qualifications',
    headerName: intl.formatMessage({ id: `${localizationSubset}.table.fields.qualifications.heading` }),
    minWidth: 300,
    resizable: false,
    sortable: true,
    filterable: false,
    disableColumnMenu: true,
    renderCell: (params) => {
      if (!params.value) {
        return null;
      }
      const row = params.row as TemplateListItem;
      return (
        <Stack
          direction={'column'}
          sx={{ height: '100%', width: '100%' }}
          justifyContent={'center'}
          alignContent={'center'}
        >
          <Stack direction={'row'} spacing={1} alignItems={'center'}>
            <Tooltip title={row.qualifications.map((q) => `(${q.abbreviation})${q.name}`).join(', ')}>
              <Box sx={{ textOverflow: 'ellipsis', overflow: 'hidden', whiteSpace: 'nowrap' }}>
                {row.qualifications.map((qualification) => (
                  <LightChip
                    key={qualification.id}
                    label={qualification.abbreviation}
                    color="default"
                    size="small"
                    sx={{ textTransform: 'capitalize' }}
                  />
                ))}
              </Box>
            </Tooltip>
            <Box flex={1} />
            <IconButton
              onClick={(evt) => {
                evt.stopPropagation();
                handleCopyClick(row);
              }}
              sx={{ width: 32, height: 32 }}
            >
              <FaIcon name="copy" height={16} width={16} />
            </IconButton>
            <IconButton sx={{ width: 32, height: 32 }}>
              <FaIcon name="angle-right" height={10} width={10} />
            </IconButton>
          </Stack>
        </Stack>
      );
    },
  };
}
